<template>
	<!-- The detail page of the workbench, rendered as a pane body inside the
	     shared tool rail: the rail's header already prints the title and the
	     rail's body already pads and scrolls, so this is only the reading
	     surface itself. One thing is on it at a time — what is being authored
	     outranks what is selected, because authoring is the operator's newest
	     intent and the selection behind it is only the context it was born
	     from. -->
	<div>
		<template v-if="authoring">
			<RouteCreateForm
				v-if="authoring.kind === 'route'"
				:users="users"
				:assignments="assignments"
				:sites="sites"
				@notify="forwardNotify"
				@created="onRouteCreated"
				@cancel="emit('close')"
			/>
			<AssignmentForm
				v-else
				ref="assignmentForm"
				:users="users"
				:sites="sites"
				:trap-configurations="trapConfigurations"
				:targets="targets"
				:pick-mode="pickMode"
				@notify="forwardNotify"
				@created="onAssignmentCreated"
				@update:targets="emit('update:targets', $event)"
				@update:pickMode="emit('update:pickMode', $event)"
			/>
		</template>

		<WorkDetails
			v-else-if="selectedWorkRows.length > 0"
			:work="selectedWorkRows"
			:users="users"
			@assigned="emit('changed')"
			@clear="emit('close')"
			@notify="forwardNotify"
		/>

		<AssignmentDetails
			v-else-if="selectedAssignment"
			:assignment="selectedAssignment"
			:users="users"
			:sites="sites"
			:trap-configurations="trapConfigurations"
			:routes="routes"
			@notify="forwardNotify"
			@changed="emit('changed')"
			@select="emit('select', $event)"
		/>

		<RouteDetails
			v-else-if="selectedRoute"
			:route="selectedRoute"
			:users="users"
			:sites="sites"
			:assignments="assignments"
			@notify="forwardNotify"
			@changed="emit('changed')"
			@select="emit('select', $event)"
		/>

		<TrapDetails
			v-else-if="selectedTrap"
			:trap="selectedTrap"
			@collect="onCollect"
		/>

		<StationDetails
			v-else-if="selectedStation"
			:station-id="selectedStation.id"
			@select="emit('select', $event)"
			@notify="forwardNotify"
			@set-trap="onSetTrap"
		/>

		<TechnicianDetails
			v-else-if="selectedTechnician"
			:technician="selectedTechnician"
			:assignments="assignments"
			:routes="routes"
			:sites="sites"
			@select="emit('select', $event)"
			@assign-work="onAssignWork"
		/>

		<!-- Two different silences: nothing chosen yet is the page waiting for
		     the operator, while a selection whose row the last reload dropped is
		     a stale name the collection can no longer look up. Saying which one
		     it is keeps an empty panel from reading as a broken one. -->
		<p v-else-if="selection" class="text-body-secondary small mb-0">
			This selection is no longer loaded. Close the panel or choose another
			entry.
		</p>
		<p v-else class="text-body-secondary small mb-0">
			Select an assignment, route, technician, station or trap to read its
			details.
		</p>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import type {
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsWorkRow,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
} from "@/type/api";

import AssignmentDetails from "./AssignmentDetails.vue";
import AssignmentForm from "./AssignmentForm.vue";
import RouteCreateForm from "./RouteCreateForm.vue";
import RouteDetails from "./RouteDetails.vue";
import StationDetails from "./StationDetails.vue";
import TechnicianDetails from "./TechnicianDetails.vue";
import TrapDetails from "./TrapDetails.vue";
import WorkDetails from "./WorkDetails.vue";
import type {
	AuthoringState,
	MapPickTarget,
	Selection,
	TargetDrawMode,
} from "./model";

const props = defineProps<{
	selection: Selection | null;
	authoring: AuthoringState | null;
	users: User[];
	sites: MapReviewSiteRow[];
	trapConfigurations: TrapConfigurationResult[];
	assignments: SyncAssignment[];
	routes: SyncRoute[];
	stations: MapReviewTrapStationRow[];
	traps: MapReviewTrapInstanceRow[];
	work: OperationsWorkRow[];
	/** A work order fetched on demand for a selection the Work queue does not
	 * carry — a footprint clicked on the map, which the queue may have no
	 * dispatchable row for. Null until one is read, and for every other kind. */
	workDetailRow?: OperationsWorkRow | null;
	selectedKeys: Set<string>;
	/** The authoring session's map targets and the map's picking mode. */
	targets: MapPickTarget[];
	pickMode: TargetDrawMode;
}>();

const emit = defineEmits<{
	select: [selection: Selection];
	close: [];
	notify: [title: string, message: string];
	changed: [];
	collect: [trap: MapReviewTrapInstanceRow];
	"assign-work": [technicianId: number];
	"set-trap": [siteId: number];
	"assignment-created": [assignment: SyncAssignment];
	"route-created": [route: SyncRoute];
	"update:targets": [targets: MapPickTarget[]];
	"update:pickMode": [mode: TargetDrawMode];
}>();

const WORK_KINDS = new Set(["work_lead", "work_order", "work_direct"]);

function workRowKind(origin: string): string {
	return origin === "lead"
		? "work_lead"
		: origin === "work_order"
			? "work_order"
			: "work_direct";
}

/** The Work queue plus the one work order read on demand. The map draws every
 * open footprint while the queue lists the work an office dispatches, so a
 * clicked footprint can name a row only this pane knows about; it joins the pool
 * the selection is filtered from, and a queue row of the same kind and id wins
 * so one work order is never listed twice. */
const workPool = computed<OperationsWorkRow[]>(() => {
	const detail = props.workDetailRow;
	if (!detail) return props.work;
	const carried = props.work.some(
		(row) =>
			workRowKind(row.origin) === workRowKind(detail.origin) &&
			row.id === detail.id,
	);
	return carried ? props.work : [detail, ...props.work];
});

const selectedWorkRows = computed(() => {
	if (!props.selection || !WORK_KINDS.has(props.selection.kind)) return [];
	const selected = workPool.value.filter((row) =>
		props.selectedKeys.has(`${workRowKind(row.origin)}:${row.id}`),
	);
	if (selected.length > 0) return selected;
	return workPool.value.filter(
		(row) =>
			workRowKind(row.origin) === props.selection?.kind &&
			row.id === props.selection?.id,
	);
});

const selectedAssignment = computed(() =>
	props.selection?.kind === "assignment"
		? props.assignments.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedRoute = computed(() =>
	props.selection?.kind === "route"
		? props.routes.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedTrap = computed(() =>
	props.selection?.kind === "trap"
		? props.traps.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedStation = computed(() =>
	props.selection?.kind === "station"
		? props.stations.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedTechnician = computed(() =>
	props.selection?.kind === "technician"
		? props.users.find((row) => row.id === props.selection?.id)
		: undefined,
);

// The authoring form is one instance that the two pre-fill doors reuse, so it
// is told what it is filling in once it is on screen (the ref exists only while
// the form is rendered).
const assignmentForm = ref<InstanceType<typeof AssignmentForm> | null>(null);

watch(
	() => props.authoring,
	async (state) => {
		await nextTick();
		const form = assignmentForm.value;
		if (!form || !state) return;
		if (state.kind === "collect") form.startCollect(state.trap);
		else if (state.kind === "assignment") {
			form.startForTechnician(state.technicianId ?? 0);
		} else if (state.kind === "set_trap") {
			form.startForSite(state.siteId);
		}
	},
);

function forwardNotify(title: string, message: string) {
	emit("notify", title, message);
}

function onCollect(trap: MapReviewTrapInstanceRow) {
	emit("collect", trap);
}

function onAssignWork(technicianId: number) {
	emit("assign-work", technicianId);
}

function onSetTrap(siteId: number) {
	emit("set-trap", siteId);
}

function onAssignmentCreated(assignment: SyncAssignment) {
	emit("assignment-created", assignment);
}

function onRouteCreated(route: SyncRoute) {
	emit("route-created", route);
}
</script>
